import { ClockCircleOutlined } from '@ant-design/icons'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useEffect, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'

import { useAppDispatch } from '../../store'
import {userAuthenticated, USER_SIGNED_OUT} from './auth-actions'
import * as toastStack from '../shared/toast-stack/actions.js'

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

interface TokenInfo {
  token: string
  expiresAt: number
  issuedAt: number
  timeLeft: number
}

interface JWTPayload {
  iat?: number
  exp?: number
  [key: string]: unknown
}

// ============================================================================
// AMPLIFY SESSION MONITOR CLASS
// ============================================================================

class AmplifySessionMonitor {
  private lastToken: string | null = null
  private sessionCheckInterval: NodeJS.Timeout | null = null

  constructor () {
    this.startSessionMonitoring()
  }

  public async logSessionEvent (event: string): Promise<void> {
    try {
      const session = await fetchAuthSession()
      const accessToken = session.tokens?.accessToken?.toString()

      if (!accessToken) {
        console.log(`🔍 [AUTH_MONITOR] ${event} - No access token available`)
        return
      }

      const tokenInfo = this.parseTokenInfo(accessToken)
      const tokenPreview = `${accessToken.slice(0, 5)}...${accessToken.slice(-5)}`
      const expiresAt = new Date(tokenInfo.expiresAt * 1000).toISOString()

      // Reduce verbosity - only log important events
      // console.log(
      //   `🔍 [AUTH_MONITOR] ${event}, ${tokenPreview}, ${this.formatTimeLeft(tokenInfo.timeLeft)}, ${expiresAt}`
      // )

      if (this.lastToken && this.lastToken !== accessToken) {
        console.log(
          `🔄 [AUTH_MONITOR] TOKEN_CHANGED, ${tokenPreview}, ${this.formatTimeLeft(tokenInfo.timeLeft)}, ${expiresAt}`
        )
      }

      this.lastToken = accessToken
    } catch (error: unknown) {
      console.error(`❌ [AUTH_MONITOR] Error logging session event ${event}:`, error)
    }
  }

  private parseTokenInfo (token: string): TokenInfo {
    try {
      const parts = token.split('.')
      if (parts.length !== 3 || !parts[1]) {
        throw new Error('Invalid token format')
      }
      const payload = JSON.parse(atob(parts[1])) as JWTPayload
      const issuedAt = payload.iat ?? 0
      const expiresAt = payload.exp ?? 0
      const currentTime = Math.floor(Date.now() / 1000)
      const timeLeft = expiresAt - currentTime

      return {
        token,
        expiresAt,
        issuedAt,
        timeLeft
      }
    } catch (error: unknown) {
      console.error('❌ [AUTH_MONITOR] Error parsing token:', error)
      return {
        token,
        expiresAt: 0,
        issuedAt: 0,
        timeLeft: 0
      }
    }
  }

  private formatTimeLeft (seconds: number): string {
    if (seconds <= 0) return 'EXPIRED'

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`
    } else {
      return `${secs}s`
    }
  }

  private startSessionMonitoring (): void {
    this.sessionCheckInterval = setInterval(() => {
      void (async () => {
        try {
          const session = await fetchAuthSession()
          const accessToken = session.tokens?.accessToken?.toString()

          if (accessToken) {
            const tokenInfo = this.parseTokenInfo(accessToken)

            if (tokenInfo.timeLeft < 300 && tokenInfo.timeLeft > 0) {
              console.log(
                `⚠️ [AUTH_MONITOR] TOKEN_EXPIRING_SOON, ${accessToken.slice(0, 5)}...${accessToken.slice(-5)}, ${this.formatTimeLeft(tokenInfo.timeLeft)}, ${new Date(tokenInfo.expiresAt * 1000).toISOString()}`
              )
            }

            if (tokenInfo.timeLeft <= 0) {
              console.log(
                `🚨 [AUTH_MONITOR] TOKEN_EXPIRED, ${accessToken.slice(0, 5)}...${accessToken.slice(-5)}, EXPIRED, ${new Date(tokenInfo.expiresAt * 1000).toISOString()}`
              )
            }
          }
        } catch (error: unknown) {
          console.error('❌ [AUTH_MONITOR] Error in session monitoring:', error)
        }
      })()
    }, 30000)
  }

  public destroy (): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval)
      this.sessionCheckInterval = null
    }
  }
}

// ============================================================================
// GLOBAL MONITOR INSTANCE AND UTILITIES
// ============================================================================

let sessionMonitor: AmplifySessionMonitor | null = null

export const createSessionMonitor = (): AmplifySessionMonitor => {
  if (!sessionMonitor) {
    sessionMonitor = new AmplifySessionMonitor()
  }
  return sessionMonitor
}

export const destroySessionMonitor = (): void => {
  if (sessionMonitor) {
    sessionMonitor.destroy()
    sessionMonitor = null
  }
}

export const logCurrentToken = async (event = 'MANUAL_CHECK'): Promise<void> => {
  if (sessionMonitor) {
    await sessionMonitor.logSessionEvent(event)
  } else {
    console.log('🔍 [AUTH_MONITOR] No session monitor available')
  }
}

// ============================================================================
// SESSION TIMEOUT TRACKING
// ============================================================================

// ============================================================================
// SESSION CONFIGURATION - Easy to adjust for production
// ============================================================================
const SESSION_TIMEOUT_KEY = 'narus_session_timeout'
const LAST_ACTIVITY_KEY = 'narus_last_activity'

// Session Configuration - 1 hour timeout with 50 minute warning for all environments
const IDLE_TIMEOUT_MS = 60 * 60 * 1000 // 1 hour for all environments
const WARNING_TIMEOUT_MS = 50 * 60 * 1000 // 50 minutes for all environments (10 min warning)

const updateLastActivity = (): void => {
  const now = Date.now()
  const timeoutTimestamp = now + IDLE_TIMEOUT_MS
  localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
  localStorage.setItem(SESSION_TIMEOUT_KEY, timeoutTimestamp.toString())

  // console.log(`🕐 [SESSION] Updated activity at ${new Date(now).toISOString()}, expires at ${new Date(timeoutTimestamp).toISOString()}`)
}

const checkSessionExpiry = (): boolean => {
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
  const sessionTimeout = localStorage.getItem(SESSION_TIMEOUT_KEY)

  if (!lastActivity || !sessionTimeout) {
    console.log('🕐 [SESSION] No previous session data found')
    return false // No previous session data
  }

  const now = Date.now()
  const timeoutTimestamp = parseInt(sessionTimeout, 10)
  const isExpired = now > timeoutTimestamp

  // console.log(`🕐 [SESSION] Check: now=${new Date(now).toISOString()}, timeout=${new Date(timeoutTimestamp).toISOString()}, expired=${String(isExpired)}`)

  return isExpired
}

const clearSessionData = (): void => {
  localStorage.removeItem(LAST_ACTIVITY_KEY)
  localStorage.removeItem(SESSION_TIMEOUT_KEY)
}

// ============================================================================
// MAIN AUTH LISTENER COMPONENT
// ============================================================================

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.50)',
    backdropFilter: 'blur(0px)',
    WebkitBackdropFilter: 'blur(0px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '200px',
    paddingBottom: '20px',
    animation: 'overlay-fade-in 0.5s forwards ease'
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '24px',
    padding: '48px 40px',
    width: '480px',
    boxShadow: '0 20px 48px rgba(0, 0, 0, 0.18)',
    textAlign: 'center' as const,
    fontFamily: 'Inter, sans-serif',
    opacity: 0,
    transform: 'translateY(20px)',
    animation: 'modal-fade-in 0.4s 0.1s forwards ease'
  },
  iconContainer: {
    marginBottom: '24px'
  },
  icon: {
    fontSize: '48px',
    color: '#1890ff',
    animation: 'icon-pulse 1.5s infinite ease-in-out'
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#111',
    marginBottom: '26px'
  },
  message: {
    fontSize: '16px',
    color: '#444',
    marginBottom: '20px'
  },
  subtext: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#888'
  }
}

export const AuthListener = (): JSX.Element | null => {
  const { authStatus, signOut: amplifySignOut, user } = useAuthenticator()
  const dispatch = useAppDispatch()
  const [ showIdleWarning, setShowIdleWarning ] = useState(false)
  const [ sessionChecked, setSessionChecked ] = useState(false)

  // Check session expiry on authentication
  useEffect(() => {
    if (authStatus === 'authenticated' && user && !sessionChecked) {
      // console.log('🕐 Checking session expiry on authentication...')

      const isExpired = checkSessionExpiry()

      if (isExpired) {
        console.log('🚨 Session expired - forcing logout')
        clearSessionData()
        amplifySignOut()
        return
      }

      // console.log('✅ Session valid - continuing authentication')
      setSessionChecked(true)
      updateLastActivity()
    }
  }, [ authStatus, user, sessionChecked, amplifySignOut ])

  // Redux integration - bridge Amplify user to Redux store
  useEffect(() => {
    if (authStatus === 'authenticated' && user && sessionChecked) {
      // console.log('✅ User authenticated, bridging to Redux store')

      void (async () => {
        try {
          // Get user attributes from the current auth session
          const session = await fetchAuthSession()
          const idToken = session.tokens?.idToken

          if (idToken) {
            // Extract payload from the ID token
            const payload = idToken.payload
            const userAttributes = {
              sub: payload.sub,
              email: payload.email,
              given_name: payload.given_name,
              family_name: payload.family_name,
              ...payload // Include any other attributes
            }

              console.log('📦 Dispatching userAuthenticated with payload:', {
                  sub: userAttributes.sub,
                  email: userAttributes.email,
                  given_name: userAttributes.given_name,
                  family_name: userAttributes.family_name,
                  fullAttributes: userAttributes
              })
            dispatch(userAuthenticated(userAttributes))
          } else {
            console.warn('⚠️ No ID token found in session')
          }
        } catch (error) {
          console.error('❌ Error extracting user attributes:', error)
        }
      })()
    } else if (authStatus === 'unauthenticated') {
      console.log('👋 User signed out, cleaning up Redux state')
      clearSessionData()
      setSessionChecked(false)
      dispatch({
        type: USER_SIGNED_OUT,
        forceRedirect: false // Don't force redirect, Amplify handles this
      })
      dispatch(toastStack.reset())
    }
  }, [ authStatus, user, dispatch, sessionChecked ])

  useEffect(() => {
    if (authStatus === 'authenticated') {
      createSessionMonitor()

      void fetchAuthSession().then(session => {
        const token = session.tokens?.accessToken?.toString()
        if (token) {
          localStorage.setItem('token', token)
          void logCurrentToken('AUTHENTICATED_INITIAL')
          setTimeout(() => {
            const currentToken = localStorage.getItem('token')
            if (currentToken) {
              void logCurrentToken('TOKEN_VERIFIED_AVAILABLE')
            } else {
              void logCurrentToken('TOKEN_NOT_FOUND_IN_STORAGE')
            }
          }, 500)
        } else {
          void logCurrentToken('NO_TOKEN_AVAILABLE_AFTER_AUTH')
        }
      }).catch((error: unknown) => {
        console.error('❌ [AUTH_MONITOR] Error fetching auth session:', error)
        void logCurrentToken('AUTH_SESSION_FETCH_ERROR')
      })
    } else if (authStatus === 'unauthenticated') {
      destroySessionMonitor()
      void logCurrentToken('USER_SIGNED_OUT')
    }
  }, [ authStatus ])

  useEffect(() => {
    const handleTokenRefresh = async (): Promise<void> => {
      try {
        const session = await fetchAuthSession()
        const token = session.tokens?.accessToken?.toString()
        if (token) {
          localStorage.setItem('token', token)
          void logCurrentToken('TOKEN_REFRESHED')
        }
      } catch (error: unknown) {
        console.error('❌ [AUTH_MONITOR] Error handling token refresh:', error)
      }
    }

    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === 'token' && e.newValue) {
        void logCurrentToken('TOKEN_UPDATED_VIA_STORAGE')
      }
    }

    const handleSessionExpiryCheck = (): void => {
      if (authStatus === 'authenticated' && sessionChecked) {
        const isExpired = checkSessionExpiry()
        if (isExpired) {
          console.log('🚨 Periodic check: Session expired - forcing logout')
          clearSessionData()
          amplifySignOut()
        }
      }
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && authStatus === 'authenticated' && sessionChecked) {
        // console.log('🔍 Tab became visible - checking session expiry')
        const isExpired = checkSessionExpiry()
        if (isExpired) {
          console.log('🚨 Visibility change: Session expired - forcing logout')
          clearSessionData()
          amplifySignOut()
        } else {
          updateLastActivity()
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    const refreshCheckInterval = setInterval(() => {
      void handleTokenRefresh()
    }, 60000)

    // Check session expiry every 5 seconds when authenticated
    const sessionCheckInterval = setInterval(handleSessionExpiryCheck, 5000)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearInterval(refreshCheckInterval)
      clearInterval(sessionCheckInterval)
    }
  }, [ authStatus, sessionChecked, amplifySignOut ])

  const handleIdle = (): void => {
    setShowIdleWarning(false)
    void logCurrentToken('USER_IDLE_TIMEOUT')
    clearSessionData()
    amplifySignOut()
  }

  const handleActive = (): void => {
    setShowIdleWarning(false)
    updateLastActivity()
    void logCurrentToken('USER_ACTIVE')
  }

  const handleAction = (): void => {
    setShowIdleWarning(false)
    updateLastActivity()
    // Log every user action for full visibility - disabled to reduce noise
    // void logCurrentToken('USER_ACTION')
  }

  const handleIdleWarning = (): void => {
    setShowIdleWarning(true)
    void logCurrentToken('IDLE_WARNING_SHOWN')
  }

  // Main idle timer - logs out after IDLE_TIMEOUT_MS
  useIdleTimer({
    onIdle: handleIdle,
    onActive: handleActive,
    onAction: handleAction,
    timeout: IDLE_TIMEOUT_MS,
    throttle: 500
  })

  // Warning timer - shows warning after WARNING_TIMEOUT_MS
  useIdleTimer({
    onIdle: handleIdleWarning,
    timeout: WARNING_TIMEOUT_MS,
    throttle: 500
  })

  if (showIdleWarning) {
    return (
      <>
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.iconContainer}>
              <ClockCircleOutlined style={styles.icon} />
            </div>
            <h2 style={styles.title}>You&rsquo;re about to be logged out</h2>
            <div style={styles.message}>
              For security reasons, you will be automatically signed out in 10 minutes due to inactivity.
            </div>
            <div style={styles.subtext}>
              Move your mouse or press any key to remain logged in.
            </div>
          </div>
        </div>
        <style>{`
  @keyframes icon-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
  }

  @keyframes overlay-fade-in {
    0% {
      background-color: rgba(0, 0, 0, 0);
      backdrop-filter: blur(0px);
    }
    100% {
      background-color: rgba(0, 0, 0, 0.25);
      backdrop-filter: blur(8px);
    }
  }

  @keyframes modal-fade-in {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`}
        </style>
      </>
    )
  }

  return null
}
